import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  Chip,
  Collapse,
  Divider,
  FormControlLabel,
  Grid,
  IconButton,
  LinearProgress,
  MenuItem,
  Radio,
  Select,
  Stack,
  Switch,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import AddRoundedIcon from '@mui/icons-material/AddRounded';
import LockRoundedIcon from '@mui/icons-material/LockRounded';
import { UpgradeModal } from '../components/UpgradeModal';
import CheckRoundedIcon from '@mui/icons-material/CheckRounded';
import WarningAmberRoundedIcon from '@mui/icons-material/WarningAmberRounded';
import ReplayRoundedIcon from '@mui/icons-material/ReplayRounded';
import LinkOffRoundedIcon from '@mui/icons-material/LinkOffRounded';
import AutoAwesomeRoundedIcon from '@mui/icons-material/AutoAwesomeRounded';
import DraftsOutlinedIcon from '@mui/icons-material/DraftsOutlined';
import SendRoundedIcon from '@mui/icons-material/SendRounded';
import SecurityRoundedIcon from '@mui/icons-material/SecurityRounded';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { StepShell } from '../components/StepShell';
import { ProviderLogo } from '../components/ProviderLogo';
import { ChoiceCard } from '../components/ChoiceCard';
import { connectMailbox } from '../services/mailboxService';
import { ImapConnectDialog } from '../components/ImapConnectDialog';
import { disconnectNudgeMailbox, saveNudgeSettings, scanNudgeMailbox } from '../services/appStateService';
import { useApp } from '../context/AppContext';
import { ViralShareCard } from '../components/ViralShareCard';
import TrustedLogo from '../images/trusted-site.png';
import Soc2Logo from '../images/soc2-logo.png';
import ISO27001Logo from '../images/iso27001-logo.png';
import GDPRLogo from '../images/gdpr-logo.png';

const providers = [
  { id: 'google', name: 'Google Workspace / Gmail', note: 'Connect with Google OAuth' },
  { id: 'microsoft', name: 'Microsoft 365 / Outlook', note: 'Connect with Microsoft OAuth' },
  { id: 'yahoo', name: 'Yahoo Mail', note: 'Provider-specific IMAP setup' },
  { id: 'icloud', name: 'iCloud Mail', note: 'Provider-specific IMAP setup' },
  { id: 'imap', name: 'Other mailbox', note: 'Connect with IMAP and SMTP' },
];

const cadenceCopy = {
  gentle: { label: 'Gentle', range: '5–7 business days', minDays: 5, maxDays: 7 },
  balanced: { label: 'Balanced', range: '3–5 business days', minDays: 3, maxDays: 5 },
  persistent: { label: 'Persistent', range: '2–3 business days', minDays: 2, maxDays: 3 },
};

const lookbackOptions = [7, 30, 60, 90, 180, 365, 730, 1460];

function lookbackLabel(days) {
  if (days === 365) return '1 year';
  if (days === 730) return '2 years';
  if (days === 1460) return '4 years';
  if (days === 30) return '30 days';
  if (days === 60) return '60 days';
  if (days === 90) return '3 months';
  if (days === 180) return '6 months';
  return `${days} days`;
}

function currentLimits(state = {}) {
  return state.subscription?.limits || {};
}

function canUseLimit(state = {}, key = '') {
  return Boolean(currentLimits(state)[key]);
}

function providerFamily(provider = '') {
  return ['imap', 'yahoo', 'icloud'].includes(provider) ? 'imap' : provider;
}

function providerAllowed(state = {}, provider = '') {
  const allowed = currentLimits(state).allowedProviderFamilies;
  if (!Array.isArray(allowed) || !allowed.length) return true;
  return allowed.includes(providerFamily(provider));
}

function maxLookbackForPlan(state = {}) {
  const limits = currentLimits(state);
  return Math.max(
    30,
    Number(limits.lookbackDays || 0),
    Number(limits.historicalLookbackDays || 0)
  );
}

function lockedLabel(label, locked) {
  if (!locked) return label;

  return (
    <Stack direction="row" spacing={0.7} alignItems="center">
      <span>{label}</span>
      <LockRoundedIcon sx={{ fontSize: 16 }} />
    </Stack>
  );
}

function mailboxNeedsReconnect(mailbox = {}) {
  const text = `${mailbox.health || ''} ${mailbox.lastScanStatus || ''} ${mailbox.lastScanError || ''}`.toLowerCase();
  return (
    mailbox.health === 'limited_permissions' ||
    mailbox.lastScanStatus === 'permission_error' ||
    text.includes('limited permission') ||
    text.includes('permission_error') ||
    (text.includes('permission') && text.includes('gmail'))
  );
}

function mailboxProviderLabel(provider = '') {
  if (provider === 'google') return 'Gmail';
  if (provider === 'microsoft') return 'Outlook';
  if (provider === 'yahoo') return 'Yahoo';
  if (provider === 'icloud') return 'iCloud';
  return 'IMAP';
}


function MailboxPrivacyAssurance() {
  return (
    <Card
      variant="outlined"
      sx={{
        mt: 2.5,
        borderColor: 'rgba(70,201,142,.34)',
        bgcolor: 'rgba(70,201,142,.065)',
        boxShadow: 'none',
      }}
    >
      <CardContent sx={{ p: { xs: 2.2, sm: 2.6 }, '&:last-child': { pb: { xs: 2.2, sm: 2.6 } } }}>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.8} alignItems={{ sm: 'flex-start' }}>
          <Box
            sx={{
              width: 44,
              height: 44,
              borderRadius: 2.5,
              display: 'grid',
              placeItems: 'center',
              flex: '0 0 auto',
              bgcolor: 'rgba(70,201,142,.14)',
              color: 'success.dark',
            }}
          >
            <SecurityRoundedIcon />
          </Box>

          <Box minWidth={0} flex={1}>
            <Typography fontWeight={850} sx={{ fontSize: 17 }}>
              Your mailbox stays private and under your control
            </Typography>

            <Typography variant="body2" color="text.secondary" sx={{ mt: .65, lineHeight: 1.65 }}>
              Nudge uses mailbox access only to identify sent conversations that may still need a reply and to create
              follow-up drafts. We do not sell your email data or use your emails to train AI models. Nudge does not
              keep a permanent copy of your full mailbox, and you can disconnect access at any time.
            </Typography>

            <Stack direction="row" flexWrap="wrap" useFlexGap gap={1} sx={{ mt: 1.5 }}>
              {[
                'Draft mode by default',
                'Only relevant thread context is processed',
                'Stops when a reply is detected',
              ].map((label) => (
                <Chip
                  key={label}
                  size="small"
                  icon={<CheckRoundedIcon />}
                  label={label}
                  sx={{
                    bgcolor: 'rgba(255,255,255,.72)',
                    border: '1px solid rgba(70,201,142,.24)',
                    '& .MuiChip-icon': { color: 'success.main' },
                  }}
                />
              ))}
            </Stack>

            <Stack direction="row" flexWrap="wrap" useFlexGap gap={2} sx={{ mt: 1.6 }}>
              <Typography
                component="a"
                href="https://nudgeforme.com/privacy.html"
                target="_blank"
                rel="noopener noreferrer"
                variant="body2"
                fontWeight={750}
                color="primary.dark"
                sx={{ textDecoration: 'underline', textUnderlineOffset: 3 }}
              >
                Read our privacy policy
              </Typography>

              <Typography
                component="a"
                href="https://nudgeforme.com/data-deletion.html"
                target="_blank"
                rel="noopener noreferrer"
                variant="body2"
                fontWeight={750}
                color="primary.dark"
                sx={{ textDecoration: 'underline', textUnderlineOffset: 3 }}
              >
                Data deletion instructions
              </Typography>
            </Stack>
            <Box
              sx={{
                mt: 2.2,
                pt: 2,
                borderTop: '1px solid rgba(70,201,142,.18)',
              }}
            >
              <Typography
                variant="body1"
                color="text.secondary"
                fontWeight={750}
                sx={{ display: 'block', mb: 1.4 }}
              >
                Built on Snoooz’s audited security infrastructure
              </Typography>

              <Stack
                direction="row"
                alignItems="center"
                flexWrap="wrap"
                useFlexGap
                gap={{ xs: 1.3, sm: 2 }}
              >
                <Box
                  component="a"
                  href="https://www.trustedsite.com/verify?js=1&host=nudgeforme.com"
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="Verify NudgeForMe on TrustedSite"
                  sx={{ display: 'inline-flex' }}
                >
                  <Box
                    component="img"
                    src={TrustedLogo}
                    alt="TrustedSite verified"
                    sx={{ width: 105, height: 'auto' }}
                  />
                </Box>

                <Box
                  component="img"
                  src={Soc2Logo}
                  alt="Snoooz SOC 2 Type II audited"
                  title="NudgeForMe runs on Snoooz infrastructure. NudgeForMe will be included in the next annual audit scope."
                  sx={{
                    height: { xs: 45, sm: 52 },
                    width: 'auto',
                    objectFit: 'contain',
                  }}
                />

                <Box
                  component="img"
                  src={ISO27001Logo}
                  alt="Snoooz ISO/IEC 27001 certified"
                  title="NudgeForMe is operated using Snoooz's ISO/IEC 27001 security management system."
                  sx={{
                    height: { xs: 45, sm: 52 },
                    width: 'auto',
                    objectFit: 'contain',
                  }}
                />

                <Box
                  component="img"
                  src={GDPRLogo}
                  alt="GDPR privacy controls"
                  sx={{
                    height: { xs: 43, sm: 50 },
                    width: 'auto',
                    objectFit: 'contain',
                  }}
                />
              </Stack>

              {/* <Typography
                variant="caption"
                color="text.secondary"
                sx={{
                  display: 'block',
                  mt: 1.3,
                  maxWidth: 650,
                  lineHeight: 1.55,
                }}
              >
                NudgeForMe uses Snoooz infrastructure and security controls.
              </Typography> */}
            </Box>
          </Box>
        </Stack>
      </CardContent>
    </Card>
  );
}

function MailboxStep({ onBack, onNext, onUpgrade }) {
  const { state, refreshAppState } = useApp();
  const [connecting, setConnecting] = useState('');
  const [disconnecting, setDisconnecting] = useState('');
  const [imapProvider, setImapProvider] = useState('');
  const [showProviders, setShowProviders] = useState(false);
  const [error, setError] = useState('');
  const activeWorkspaceId = state.activeWorkspaceId || state.defaultWorkspaceId || '';
  const allConnectedMailboxes = state.mailboxes || [];
  const connectedMailboxes = allConnectedMailboxes.filter((mailbox) => !activeWorkspaceId || mailbox.workspaceId === activeWorkspaceId);
  const usableMailboxes = connectedMailboxes.filter((mailbox) => !mailboxNeedsReconnect(mailbox));
  const hasConnectedMailbox = connectedMailboxes.length > 0;

  const limits = currentLimits(state);
  const mailboxLimit = Math.max(1, Number(limits.mailboxes || 1));
  const activeMailboxCount = allConnectedMailboxes.filter((mailbox) => !mailboxNeedsReconnect(mailbox) && mailbox.status !== 'disconnected').length;
  const mailboxLimitReached = activeMailboxCount >= mailboxLimit;

  const openMailboxUpgrade = () => {
    onUpgrade?.(
      `Your current plan includes ${mailboxLimit} mailbox${mailboxLimit === 1 ? '' : 'es'}. Upgrade your plan to connect more.`
    );
  };

  const showProviderChoices = () => {
    if (mailboxLimitReached) {
      openMailboxUpgrade();
      return;
    }

    setShowProviders(true);
  };

  const connect = async (provider, loginHint = '') => {
    setError('');

    if (!providerAllowed(state, provider)) {
      onUpgrade?.('IMAP, Yahoo, and iCloud mailbox connections are not included in your current plan.');
      return;
    }

    // Reconnecting an existing mailbox is allowed even when the plan is at capacity.
    if (!loginHint && mailboxLimitReached) {
      openMailboxUpgrade();
      return;
    }

    if (['yahoo', 'icloud', 'imap'].includes(provider)) {
      setImapProvider(provider);
      return;
    }

    setConnecting(provider);
    try {
      const result = await connectMailbox(provider, state.user?.email, {
        workspaceId: activeWorkspaceId,
        loginHint,
      });
      if (!result?.redirecting) await refreshAppState();
    } catch (err) {
      setError(err.message || 'Could not connect mailbox.');
    } finally {
      setConnecting('');
    }
  };

  const disconnect = async (mailbox) => {
    const email = mailbox?.email || '';
    const provider = mailbox?.provider || '';
    const mailboxId = mailbox?.mailboxId || mailbox?.id || '';
    const confirmed = window.confirm(`Disconnect ${email || 'this mailbox'} from Nudge? You can reconnect it anytime.`);
    if (!confirmed) return;

    setError('');
    setDisconnecting(mailboxId || `${provider}-${email}`);
    try {
      await disconnectNudgeMailbox({ mailboxId, id: mailboxId, provider, email });
      await refreshAppState();
      setShowProviders(true);
    } catch (err) {
      setError(err?.message || 'Could not disconnect mailbox.');
    } finally {
      setDisconnecting('');
    }
  };

  return (
    <StepShell
      step={1}
      total={6}
      title="Connect the mailbox Nudge should watch."
      description="Connect securely through your email provider. Existing Snoooz mailbox connections appear automatically, and you can disconnect access at any time."
      onBack={onBack}
      onNext={onNext}
      nextDisabled={!usableMailboxes.length}
    >
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {hasConnectedMailbox && !showProviders ? (
        <Stack spacing={1.5}>
          {connectedMailboxes.map((mailbox) => {
            const limited = mailboxNeedsReconnect(mailbox);
            return (
              <Card key={mailbox.mailboxId || mailbox.id || `${mailbox.provider}-${mailbox.email}`} sx={{ borderColor: limited ? 'warning.main' : 'divider' }}>
                <CardContent sx={{ p: 2.4 }}>
                  <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" alignItems={{ sm: 'center' }} spacing={2}>
                    <Stack direction="row" spacing={1.6} alignItems="center" minWidth={0}>
                      <ProviderLogo provider={mailbox.provider} />
                      <Box minWidth={0}>
                        <Typography fontWeight={800} noWrap>{mailbox.email}</Typography>
                        <Stack direction="row" spacing={.8} alignItems="center" flexWrap="wrap" useFlexGap sx={{ mt: .5 }}>
                          {limited ? (
                            <>
                              <WarningAmberRoundedIcon color="warning" fontSize="small" />
                              <Typography variant="body2" color="warning.dark" fontWeight={750}>Limited permissions</Typography>
                            </>
                          ) : (
                            <>
                              <CheckRoundedIcon color="success" fontSize="small" />
                              <Typography variant="body2" color="success.dark" fontWeight={750}>{mailboxProviderLabel(mailbox.provider)} connected</Typography>
                            </>
                          )}
                        </Stack>
                        {limited && (
                          <Typography variant="body2" color="text.secondary" sx={{ mt: .8 }}>
                            Reconnect and select the Gmail read/send permission checkbox so Nudge can scan sent mail and create drafts.
                          </Typography>
                        )}
                      </Box>
                    </Stack>
                    <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                      {limited && (
                        <Button variant="contained" size="small" startIcon={<ReplayRoundedIcon />} onClick={() => connect(mailbox.provider, mailbox.email)} disabled={Boolean(connecting)}>
                          {connecting === mailbox.provider ? 'Reconnecting…' : 'Reconnect'}
                        </Button>
                      )}
                      <Button variant="outlined" size="small" onClick={showProviderChoices}>
                        {mailboxLimitReached ? 'Add another mailbox' : 'Add another'}
                      </Button>
                      <Button
                        variant="outlined"
                        color="inherit"
                        size="small"
                        startIcon={<LinkOffRoundedIcon />}
                        onClick={() => disconnect(mailbox)}
                        disabled={Boolean(disconnecting)}
                      >
                        {disconnecting === (mailbox.mailboxId || mailbox.id) ? 'Disconnecting…' : 'Disconnect'}
                      </Button>
                    </Stack>
                  </Stack>
                </CardContent>
              </Card>
            );
          })}
          {!usableMailboxes.length && (
            <Alert severity="warning">Reconnect your mailbox with full Gmail permissions before continuing.</Alert>
          )}
        </Stack>
      ) : (
        <Grid container spacing={2}>
          {providers.map((provider) => {
            const providerMailboxes = connectedMailboxes.filter((item) => item.provider === provider.id);
            const limitedMailboxes = providerMailboxes.filter(mailboxNeedsReconnect);
            const planAllowsProvider = providerAllowed(state, provider.id);
            return (
              <Grid item xs={12} sm={6} key={provider.id}>
                <Card sx={{ height: '100%', borderColor: limitedMailboxes.length ? 'warning.main' : 'divider' }}>
                  <CardContent sx={{ p: 2.4 }}>
                    <Stack direction="row" spacing={1.7} alignItems="center">
                      <ProviderLogo provider={provider.id} />
                      <Box flex={1} minWidth={0}>
                        <Typography variant="h4" sx={{ fontSize: 17.5 }}>{provider.name}</Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ mt: .4 }}>{provider.note}</Typography>
                        {!planAllowsProvider && <Chip size="small" label="Upgrade required" sx={{ mt: .8 }} />}
                      </Box>
                    </Stack>

                    {providerMailboxes.length > 0 && (
                      <Stack spacing={.8} mt={2}>
                        {providerMailboxes.map((mailbox) => {
                          const mailboxId = mailbox.mailboxId || mailbox.id;
                          const limited = mailboxNeedsReconnect(mailbox);
                          return (
                            <Stack key={mailboxId} direction="row" spacing={1} alignItems="center" justifyContent="space-between" sx={{ p: .9, bgcolor: 'rgba(16,18,40,.035)', borderRadius: 1.2 }}>
                              <Box minWidth={0}>
                                <Typography variant="body2" fontWeight={750} noWrap>{mailbox.email}</Typography>
                                <Typography variant="caption" color={limited ? 'warning.dark' : 'success.dark'}>{limited ? 'Needs reconnect' : 'Connected'}</Typography>
                              </Box>
                              <Stack direction="row" spacing={.5}>
                                {limited && provider.id !== 'imap' && provider.id !== 'yahoo' && provider.id !== 'icloud' && (
                                  <Button size="small" onClick={() => connect(provider.id, mailbox.email)}>Reconnect</Button>
                                )}
                                <Button size="small" color="inherit" onClick={() => disconnect(mailbox)} disabled={Boolean(disconnecting)}>Disconnect</Button>
                              </Stack>
                            </Stack>
                          );
                        })}
                      </Stack>
                    )}

                    <Button
                      variant="outlined"
                      size="small"
                      startIcon={<AddRoundedIcon />}
                      sx={{ mt: 2.2 }}
                      onClick={() => connect(provider.id)}
                      disabled={Boolean(connecting)}
                    >
                      {!planAllowsProvider ? 'Upgrade to connect' : connecting === provider.id ? 'Connecting…' : providerMailboxes.length ? `Connect another ${mailboxProviderLabel(provider.id)}` : 'Connect'}
                    </Button>
                  </CardContent>
                </Card>
              </Grid>
            );
          })}
        </Grid>
      )}

      <MailboxPrivacyAssurance />

      <ImapConnectDialog
        open={Boolean(imapProvider)}
        provider={imapProvider || 'imap'}
        initialEmail={state.user?.email || ''}
        onClose={() => setImapProvider('')}
        onConnected={refreshAppState}
      />
    </StepShell>
  );
}

function TargetsStep({ onBack, onNext, onUpgrade }) {
  const { state, updateSettings } = useApp();
  const settings = state.settings;
  const maxLookbackDays = maxLookbackForPlan(state);
  const [input, setInput] = useState('');

  const addTarget = () => {
    const value = input.trim().toLowerCase().replace(/^@/, '');
    if (!value || settings.specificTargets.includes(value)) return;
    updateSettings({ specificTargets: [...settings.specificTargets, value] });
    setInput('');
  };

  const remove = (value) => updateSettings({ specificTargets: settings.specificTargets.filter((item) => item !== value) });

  return (
    <StepShell
      step={2}
      total={6}
      title="Who should Nudge follow up with?"
      description="Choose people you always want Nudge to watch, let Nudge find other opportunities automatically, or use both."
      onBack={onBack}
      onNext={onNext}
      nextDisabled={!settings.automaticDiscovery && !settings.specificTargets.length}
    >
      <Stack spacing={2.2}>
        <Card>
          <CardContent sx={{ p: 3 }}>
            <Stack direction="row" justifyContent="space-between" gap={2} alignItems="flex-start">
              <Box>
                <Typography variant="h4" sx={{ fontSize: 20 }}>Always watch these people or domains</Typography>
                <Typography color="text.secondary" sx={{ mt: .6 }}>Nudge will include your sent conversations with these people even when they are too recent for automatic discovery. The actual follow-up still waits for your selected cadence.</Typography>
              </Box>
              <Switch checked={settings.specificTargets.length > 0} disabled />
            </Stack>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.2} mt={2.3}>
              <TextField
                fullWidth
                value={input}
                onChange={(event) => setInput(event.target.value)}
                onKeyDown={(event) => { if (event.key === 'Enter') { event.preventDefault(); addTarget(); } }}
                placeholder="alex@example.com or example.com"
              />
              <Button variant="outlined" startIcon={<AddRoundedIcon />} onClick={addTarget}>Add</Button>
            </Stack>
            <Stack direction="row" flexWrap="wrap" gap={1} mt={2}>
              {settings.specificTargets.map((value) => (
                <Chip key={value} label={value.includes('@') ? value : `@${value}`} onDelete={() => remove(value)} />
              ))}
            </Stack>
          </CardContent>
        </Card>

        <Card sx={{ borderColor: settings.automaticDiscovery ? 'primary.dark' : 'divider' }}>
          <CardContent sx={{ p: 3 }}>
            <Stack direction="row" justifyContent="space-between" gap={2} alignItems="flex-start">
              <Box>
                <Typography variant="h4" sx={{ fontSize: 20 }}>Find conversations automatically</Typography>
                <Typography color="text.secondary" sx={{ mt: .6 }}>Let Nudge identify other sent conversations that appear to need a follow-up.</Typography>
              </Box>
              <Switch checked={settings.automaticDiscovery} onChange={(event) => updateSettings({ automaticDiscovery: event.target.checked })} />
            </Stack>
            <Collapse in={settings.automaticDiscovery}>
              <Stack direction={{ xs: 'column', sm: 'row' }} alignItems={{ sm: 'center' }} gap={1.5} mt={2.3}>
                <Typography fontWeight={700}>Check the last</Typography>
                <ToggleButtonGroup
                  exclusive
                  value={settings.lookbackDays}
                  onChange={(_, value) => {
                    if (!value) return;

                    if (Number(value) > maxLookbackDays) {
                      onUpgrade?.(`Your current plan includes up to ${lookbackLabel(maxLookbackDays)} of history. Upgrade your plan for a longer historical lookback.`);
                      return;
                    }

                    updateSettings({ lookbackDays: value });
                  }}
                  size="small"
                >
                  {lookbackOptions.map((days) => {
                    const locked = days > maxLookbackDays;

                    return (
                      <ToggleButton key={days} value={days}>
                        {lockedLabel(lookbackLabel(days), locked)}
                      </ToggleButton>
                    );
                  })}
                </ToggleButtonGroup>
              </Stack>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1.7 }}>
                Nudge ignores newsletters, automated senders, no-reply addresses, and conversations that do not appear to require a reply.
              </Typography>
            </Collapse>
          </CardContent>
        </Card>
      </Stack>
    </StepShell>
  );
}

function ModeStep({ onBack, onNext, onUpgrade }) {
  const { state, updateSettings } = useApp();
  const mode = state.settings.mode;
  const canAutoSend = canUseLimit(state, 'autoSend');

  const chooseSendMode = () => {
    if (!canAutoSend) {
      onUpgrade?.('Send automatically is a Pro feature.');
      return;
    }

    updateSettings({
      mode: 'send',
      autoSendPolicy: state.settings.autoSendPolicy || 'approval',
    });
  };

  return (
    <StepShell
      step={3}
      total={6}
      title="How should Nudge handle follow-ups?"
      description="You can change this at any time. New accounts start in Draft mode so you can see the quality before enabling automatic sending."
      onBack={onBack}
      onNext={onNext}
    >
      <Grid container spacing={2}>
        <Grid item xs={12} sm={6}>
          <ChoiceCard
            selected={mode === 'draft'}
            onClick={() => updateSettings({ mode: 'draft' })}
            title="Draft in my mailbox"
            description="Nudge writes the follow-up and saves it inside your normal Drafts folder. You review and send it from Gmail, Outlook, or your usual email app."
            badge="Recommended"
            icon={<Box sx={{ width: 48, height: 48, borderRadius: 3, display: 'grid', placeItems: 'center', bgcolor: 'rgba(141,149,255,.14)', color: 'secondary.main' }}><DraftsOutlinedIcon /></Box>}
          />
        </Grid>

        <Grid item xs={12} sm={6}>
          <ChoiceCard
            selected={mode === 'send' && canAutoSend}
            onClick={chooseSendMode}
            title={canAutoSend ? 'Send automatically' : 'Send automatically · Pro'}
            description="Nudge writes and sends follow-ups using your rules. It stops when a reply, bounce, or other stop signal is detected."
            badge={canAutoSend ? undefined : 'Pro'}
            icon={<Box sx={{ width: 48, height: 48, borderRadius: 3, display: 'grid', placeItems: 'center', bgcolor: 'rgba(255,182,72,.15)', color: 'primary.dark' }}><SendRoundedIcon /></Box>}
          />
        </Grid>
      </Grid>
    </StepShell>
  );
}

function BehaviorStep({ onBack, onNext, onUpgrade }) {
  const { state, updateSettings } = useApp();
  const s = state.settings;

  const canAutoCreateDrafts = canUseLimit(state, 'autoCreateDrafts');
  const canUseAttachments = canUseLimit(state, 'includeThreadAttachments');
  const canUseCustomInstructions = canUseLimit(state, 'customInstructions');
  const maxFollowUps = Math.max(1, Number(currentLimits(state).maxFollowUps || 10));
  const canContinueUntilReply = Boolean(currentLimits(state).allowContinueUntilReply);
  const canUseCustomCadence = Boolean(currentLimits(state).customCadence);

  const setCadence = (value) => {
    if (value === 'custom') {
      if (!canUseCustomCadence) {
        onUpgrade?.('Custom follow-up timing is not included in your current plan.');
        return;
      }
      updateSettings({ cadence: 'custom' });
      return;
    }
    const selected = cadenceCopy[value];
    updateSettings({ cadence: value, minDays: selected.minDays, maxDays: selected.maxDays });
  };

  return (
    <StepShell
      step={4}
      total={6}
      title="Set your follow-up rhythm."
      description="Choose a simple default. Smart timing can adjust within these boundaries based on recipient behaviour and out-of-office replies."
      onBack={onBack}
      onNext={onNext}
    >
      <Stack spacing={2.2}>
        <Card>
          <CardContent sx={{ p: 3 }}>
            <Typography variant="h4" sx={{ fontSize: 20 }}>Follow-up attempts</Typography>
            <ToggleButtonGroup
              exclusive
              value={s.continueUntilReply && canContinueUntilReply ? 'until-reply' : Math.min(Number(s.attempts || 3), maxFollowUps)}
              onChange={(_, value) => {
                if (!value) return;
                if (value === 'until-reply') updateSettings({ continueUntilReply: true, attempts: maxFollowUps });
                else updateSettings({ continueUntilReply: false, attempts: value });
              }}
              sx={{ mt: 2, flexWrap: 'wrap', gap: 1, '& .MuiToggleButtonGroup-grouped': { border: '1px solid rgba(26,29,49,.13)!important', borderRadius: '999px!important', px: 2 } }}
            >
              {[3, 5, 7].filter((value) => value <= maxFollowUps).map((value) => <ToggleButton key={value} value={value}>{value} follow-ups</ToggleButton>)}
              {canContinueUntilReply && <ToggleButton value="until-reply">Until reply</ToggleButton>}
            </ToggleButtonGroup>
            {s.continueUntilReply && canContinueUntilReply && <Alert severity="info" sx={{ mt: 2 }}>Nudge continues until a reply, bounce, out-of-office pause, monthly allowance, or another safety stop.</Alert>}
          </CardContent>
        </Card>

        <Card>
          <CardContent sx={{ p: 3 }}>
            <Typography variant="h4" sx={{ fontSize: 20 }}>Timing</Typography>
            <Grid container spacing={1.5} mt={.5}>
              {Object.entries(cadenceCopy).map(([id, item]) => (
                <Grid item xs={12} sm={canUseCustomCadence ? 3 : 4} key={id}>
                  <ChoiceCard selected={s.cadence === id} onClick={() => setCadence(id)} title={item.label} description={item.range} />
                </Grid>
              ))}
              {canUseCustomCadence && (
                <Grid item xs={12} sm={3}>
                  <ChoiceCard selected={s.cadence === 'custom'} onClick={() => setCadence('custom')} title="Custom" description="Choose your own range" />
                </Grid>
              )}
            </Grid>
            {s.cadence === 'custom' && canUseCustomCadence && (
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.2} sx={{ mt: 2 }}>
                <TextField size="small" type="number" label="Minimum days" value={s.minDays || 3} onChange={(event) => updateSettings({ minDays: Math.max(1, Number(event.target.value || 1)) })} />
                <TextField size="small" type="number" label="Maximum days" value={s.maxDays || 5} onChange={(event) => updateSettings({ maxDays: Math.max(1, Number(event.target.value || 1)) })} />
              </Stack>
            )}

            <Divider sx={{ my: 2.5 }} />

            <Stack spacing={.4}>
              <FormControlLabel
                control={(
                  <Switch
                    checked={Boolean(s.autoCreateDrafts) && canAutoCreateDrafts}
                    onChange={(event) => {
                      if (event.target.checked && !canAutoCreateDrafts) {
                        onUpgrade?.('Automatically create drafts for due opportunities with Pro.');
                        return;
                      }

                      updateSettings({ autoCreateDrafts: event.target.checked });
                    }}
                  />
                )}
                label={lockedLabel('Automatically create drafts for due opportunities', !canAutoCreateDrafts)}
              />

              <Typography variant="body2" color="text.secondary" sx={{ ml: 5, mt: -.5, mb: .8 }}>
                Off by default for launch. Turn this on if you want Nudge to create drafts automatically after scheduled scans.
              </Typography>

              <FormControlLabel control={<Switch checked={s.smartTiming} onChange={(event) => updateSettings({ smartTiming: event.target.checked })} />} label="Use smart timing" />
              <FormControlLabel control={<Switch checked={s.varyNaturally} onChange={(event) => updateSettings({ varyNaturally: event.target.checked })} />} label="Vary timing naturally" />
              <FormControlLabel control={<Switch checked={s.recipientLocalTime} onChange={(event) => updateSettings({ recipientLocalTime: event.target.checked })} />} label="Prefer recipient local time" />
              <FormControlLabel control={<Switch checked={s.avoidWeekends} onChange={(event) => updateSettings({ avoidWeekends: event.target.checked })} />} label="Avoid weekends" />
            </Stack>
          </CardContent>
        </Card>

        <Card>
          <CardContent sx={{ p: 3 }}>
            <Typography variant="h4" sx={{ fontSize: 20 }}>Writing instructions</Typography>
            <Typography color="text.secondary" sx={{ mt: .6 }}>
              Optional. Tell Nudge how your follow-ups should sound. Safety rules still win: no invented facts, no signatures, and Nudge stops on replies.
            </Typography>

            <TextField
              fullWidth
              multiline
              minRows={4}
              disabled={!canUseCustomInstructions}
              value={canUseCustomInstructions ? s.customInstructions || '' : ''}
              onChange={(event) => updateSettings({ customInstructions: event.target.value.slice(0, 1200) })}
              placeholder={
                canUseCustomInstructions
                  ? "Example: Keep it friendly and specific. Mention the last open question when possible. Avoid 'just bumping this up'. Keep it under 3 sentences."
                  : 'Upgrade to Pro to add follow-up writing instructions.'
              }
              inputProps={{ maxLength: 1200 }}
              sx={{ mt: 2 }}
            />

            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
              {(canUseCustomInstructions ? s.customInstructions || '' : '').length}/1200 characters
            </Typography>

            {!canUseCustomInstructions && (
              <Button
                size="small"
                variant="outlined"
                startIcon={<LockRoundedIcon />}
                onClick={() => onUpgrade?.('Custom follow-up writing instructions are a Pro feature.')}
                sx={{ mt: 1.5 }}
              >
                Upgrade to customize writing style
              </Button>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardContent sx={{ p: 3 }}>
            <Typography variant="h4" sx={{ fontSize: 20 }}>Draft content</Typography>
            <Typography color="text.secondary" sx={{ mt: .6 }}>
              Optional. These can also be changed later in Settings.
            </Typography>

            <Stack spacing={1.2} mt={2}>
              <FormControlLabel
                control={<Switch checked={Boolean(s.includeThreadHistoryInDrafts)} onChange={(event) => updateSettings({ includeThreadHistoryInDrafts: event.target.checked })} />}
                label="Include previous thread below the follow-up"
              />

              <FormControlLabel
                control={(
                  <Switch
                    checked={Boolean(s.includeThreadAttachments) && canUseAttachments}
                    onChange={(event) => {
                      if (event.target.checked && !canUseAttachments) {
                        onUpgrade?.('Including attachments from previous sent emails is a Pro feature.');
                        return;
                      }

                      updateSettings({ includeThreadAttachments: event.target.checked });
                    }}
                  />
                )}
                label={lockedLabel('Include small attachments from the previous sent email when possible', !canUseAttachments)}
              />
            </Stack>
          </CardContent>
        </Card>
      </Stack>
    </StepShell>
  );
}

function SafetyStep({ onBack, onNext }) {
  const { state, updateSettings } = useApp();
  const s = state.settings;

  const [blockedInput, setBlockedInput] = useState('');
  const [allowedInput, setAllowedInput] = useState('');
  const [advanced, setAdvanced] = useState(false);

  const addBlocked = () => {
    const raw = blockedInput.trim().toLowerCase();
    if (!raw) return;

    if (raw.includes('@') && !raw.startsWith('@')) {
      updateSettings({ blockedEmails: [...new Set([...(s.blockedEmails || []), raw])] });
    } else {
      const domain = raw.replace(/^@/, '');
      if (domain) updateSettings({ blockedDomains: [...new Set([...(s.blockedDomains || []), domain])] });
    }

    setBlockedInput('');
  };

  const addAllowed = () => {
    const raw = allowedInput.trim().toLowerCase();
    if (!raw) return;

    if (raw.includes('@') && !raw.startsWith('@')) {
      updateSettings({ allowedEmails: [...new Set([...(s.allowedEmails || []), raw])] });
    } else {
      const domain = raw.replace(/^@/, '');
      if (domain) updateSettings({ allowedDomains: [...new Set([...(s.allowedDomains || []), domain])] });
    }

    setAllowedInput('');
  };

  return (
    <StepShell
      step={5}
      total={6}
      title="Keep follow-ups safe."
      description={s.mode === 'send'
        ? 'Choose who Nudge is allowed to send to. Reply, bounce, out-of-office, and no-reply protections are always enforced.'
        : 'Reply, bounce, out-of-office, and no-reply protections are always enforced.'}
      onBack={onBack}
      onNext={onNext}
    >
      <Stack spacing={2.2}>
        {s.mode === 'send' && (
          <Card>
            <CardContent sx={{ p: 3 }}>
              <Typography variant="h4" sx={{ fontSize: 20 }}>Auto-send permission</Typography>
              <Typography color="text.secondary" variant="body2" sx={{ mt: .55 }}>
                Start conservatively. You can broaden this later from Settings.
              </Typography>

              <ToggleButtonGroup
                exclusive
                value={s.autoSendPolicy || 'approval'}
                onChange={(_, value) => value && updateSettings({ autoSendPolicy: value })}
                sx={{
                  mt: 2,
                  display: 'flex',
                  flexWrap: 'wrap',
                  gap: 1,
                  '& .MuiToggleButtonGroup-grouped': {
                    border: '1px solid rgba(26,29,49,.13)!important',
                    borderRadius: '999px!important',
                    px: 1.8,
                  },
                }}
              >
                <ToggleButton value="approval">Approve each · recommended</ToggleButton>
                <ToggleButton value="allowlist">Allow-list only</ToggleButton>
                <ToggleButton value="all">Anyone except blocked</ToggleButton>
              </ToggleButtonGroup>

              {(s.autoSendPolicy || 'approval') === 'approval' && (
                <Alert severity="success" sx={{ mt: 2 }}>
                  Nudge will wait for you to approve each conversation before the scheduled send.
                </Alert>
              )}

              {(s.autoSendPolicy || 'approval') === 'allowlist' && (
                <Box sx={{ mt: 2 }}>
                  <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
                    <TextField
                      fullWidth
                      size="small"
                      value={allowedInput}
                      onChange={(event) => setAllowedInput(event.target.value)}
                      onKeyDown={(event) => {
                        if (event.key === 'Enter') {
                          event.preventDefault();
                          addAllowed();
                        }
                      }}
                      placeholder="person@example.com or @example.com"
                    />
                    <Button variant="outlined" onClick={addAllowed}>Allow</Button>
                  </Stack>

                  <Stack direction="row" gap={1} flexWrap="wrap" mt={1.4}>
                    {(s.allowedEmails || []).map((value) => (
                      <Chip key={value} label={value} onDelete={() => updateSettings({ allowedEmails: (s.allowedEmails || []).filter((item) => item !== value) })} />
                    ))}
                    {(s.allowedDomains || []).map((value) => (
                      <Chip key={value} label={`@${value}`} onDelete={() => updateSettings({ allowedDomains: (s.allowedDomains || []).filter((item) => item !== value) })} />
                    ))}
                  </Stack>

                  {!(s.allowedEmails || []).length && !(s.allowedDomains || []).length && (
                    <Alert severity="warning" sx={{ mt: 1.5 }}>
                      Add at least one contact or domain. Until then, Nudge will not auto-send.
                    </Alert>
                  )}
                </Box>
              )}

              {(s.autoSendPolicy || 'approval') === 'all' && (
                <Alert severity="warning" sx={{ mt: 2 }}>
                  Nudge may send to any eligible recipient unless they are blocked.
                </Alert>
              )}
            </CardContent>
          </Card>
        )}

        <Card>
          <CardContent sx={{ p: 3 }}>
            <Stack direction="row" spacing={1.4} alignItems="center" mb={1.5}>
              <Box sx={{ width: 42, height: 42, borderRadius: 2.5, display: 'grid', placeItems: 'center', bgcolor: 'rgba(70,201,142,.12)', color: 'success.main' }}>
                <SecurityRoundedIcon />
              </Box>
              <Box>
                <Typography variant="h4" sx={{ fontSize: 20 }}>Always-on protections</Typography>
                <Typography color="text.secondary" variant="body2">These cannot be disabled.</Typography>
              </Box>
            </Stack>

            <Stack spacing={.5}>
              {[
                'Stop immediately when a reply is received',
                'Pause when an out-of-office response is detected',
                'Stop when an email bounces',
                'Never contact no-reply or automated addresses',
              ].map((label) => <FormControlLabel key={label} control={<Checkbox checked disabled />} label={label} />)}
            </Stack>
          </CardContent>
        </Card>

        <Card>
          <CardContent sx={{ p: 3 }}>
            <Stack direction="row" justifyContent="space-between" alignItems="center">
              <Box>
                <Typography variant="h4" sx={{ fontSize: 20 }}>Blocklist</Typography>
                <Typography color="text.secondary" variant="body2" sx={{ mt: .5 }}>Never follow up with these addresses or domains.</Typography>
              </Box>
              <Button color="inherit" onClick={() => setAdvanced((value) => !value)}>{advanced ? 'Hide' : 'Configure'}</Button>
            </Stack>

            <Collapse in={advanced}>
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.2} mt={2.2}>
                <TextField
                  fullWidth
                  value={blockedInput}
                  onChange={(event) => setBlockedInput(event.target.value)}
                  onKeyDown={(event) => {
                    if (event.key === 'Enter') {
                      event.preventDefault();
                      addBlocked();
                    }
                  }}
                  placeholder="person@example.com or @example.com"
                />
                <Button variant="outlined" onClick={addBlocked}>Block</Button>
              </Stack>
              <Stack direction="row" gap={1} flexWrap="wrap" mt={2}>
                {(s.blockedEmails || []).map((value) => (
                  <Chip key={value} label={value} onDelete={() => updateSettings({ blockedEmails: (s.blockedEmails || []).filter((item) => item !== value) })} />
                ))}
                {(s.blockedDomains || []).map((value) => (
                  <Chip key={value} label={`@${value}`} onDelete={() => updateSettings({ blockedDomains: (s.blockedDomains || []).filter((item) => item !== value) })} />
                ))}
              </Stack>
            </Collapse>
          </CardContent>
        </Card>
      </Stack>
    </StepShell>
  );
}

function SummaryRow({ label, value }) {
  return (
    <Stack direction="row" justifyContent="space-between" alignItems="flex-start" gap={3} py={1.2}>
      <Typography color="rgba(238,240,255,.62)">{label}</Typography>
      <Typography color="#eef0ff" fontWeight={700} textAlign="right">{value}</Typography>
    </Stack>
  );
}

function ActivateStep({ onBack, onReconnect }) {
  const { state, update, refreshAppState } = useApp();
  const navigate = useNavigate();
  const [saving, setSaving] = useState(false);
  const [scanResult, setScanResult] = useState(null);
  const [statusText, setStatusText] = useState('');
  const [error, setError] = useState('');
  const permissionError = String(error || '').toLowerCase().includes('permission') || String(error || '').toLowerCase().includes('reconnect gmail');
  const s = state.settings;
  const limits = currentLimits(state);
  const effectiveMode = s.mode === 'send' && limits.autoSend ? 'send' : 'draft';
  const effectiveAutoCreateDrafts = Boolean(s.autoCreateDrafts) && Boolean(limits.autoCreateDrafts);
  const effectiveAttachments = Boolean(s.includeThreadAttachments) && Boolean(limits.includeThreadAttachments);
  const mailbox = state.mailboxes[0];
  const activate = async () => {
    setError('');
    setSaving(true);
    try {
      setStatusText('Saving your Nudge settings…');
      await saveNudgeSettings({
        settings: state.settings,
        mailboxes: state.mailboxes.filter((item) => !state.activeWorkspaceId || item.workspaceId === state.activeWorkspaceId),
        activate: true,
        workspaceId: state.activeWorkspaceId || state.defaultWorkspaceId || '',
      });

      setStatusText('Refreshing connected mailboxes…');
      const serverAfterSave = await refreshAppState();
      const activeMailboxes = serverAfterSave?.mailboxes?.length ? serverAfterSave.mailboxes : state.mailboxes;
      const workspaceId = state.activeWorkspaceId || state.defaultWorkspaceId || '';
      const scanMailbox = activeMailboxes.find((item) => (!workspaceId || item.workspaceId === workspaceId) && ['google', 'microsoft', 'imap', 'yahoo', 'icloud'].includes(item.provider));
      let latestScanResult = null;

      if (scanMailbox) {
        setStatusText('Scanning your recent sent emails…');
        latestScanResult = await scanNudgeMailbox({
          mailboxId: scanMailbox.mailboxId || scanMailbox.id,
          maxThreads: 50,
        });
        setScanResult(latestScanResult);
      } else {
        latestScanResult = {
          ok: true,
          found: 0,
          message: 'Connect a Gmail, Outlook, Yahoo, iCloud, or IMAP mailbox to start scanning.',
        };
        setScanResult(latestScanResult);
      }

      update({ onboardingComplete: true, active: true });
      setStatusText('Preparing your dashboard…');
      await refreshAppState();

      if (latestScanResult) {
        sessionStorage.setItem('nudge.lastActivationScan', JSON.stringify(latestScanResult));
      }

      navigate('/home', { replace: true });
    } catch (err) {
      setError(err?.message || 'Could not activate Nudge.');
    } finally {
      setSaving(false);
    }
  };
  return (
    <StepShell step={6} total={6} title="Ready to activate Nudge." description="Review the setup below. You can change every setting later without reconnecting your mailbox." onBack={onBack} onNext={activate} nextLabel={saving ? (statusText || 'Activating…') : 'Activate Nudge'} nextDisabled={saving}>
      {error && (
        <Alert
          severity="error"
          sx={{ mb: 2 }}
          action={permissionError ? <Button color="inherit" size="small" onClick={onReconnect}>Reconnect</Button> : null}
        >
          {error}
        </Alert>
      )}
      {statusText && <Alert severity="info" sx={{ mb: 2 }}>{statusText}</Alert>}
      {saving && <LinearProgress sx={{ mb: 2, borderRadius: 999 }} />}
      {scanResult && (
        <Alert
          severity={scanResult.found > 0 ? 'success' : 'info'}
          sx={{ mb: 2 }}
          action={<Button color="inherit" size="small" onClick={() => navigate('/home')}>Go home</Button>}
        >
          {scanResult.message || `Nudge found ${scanResult.found || 0} conversations to watch.`}
        </Alert>
      )}
      {state.referral?.code && (
        <Box sx={{ mb: 2 }}>
          <ViralShareCard referral={state.referral} region={state.region} compact />
        </Box>
      )}
      <Box className="night-panel" sx={{ position: 'relative', zIndex: 0, borderRadius: 4, p: { xs: 3, md: 4 }, boxShadow: '0 30px 80px rgba(16,18,40,.24)' }}>
        <Box sx={{ position: 'relative', zIndex: 1 }}>
          <Stack direction="row" spacing={1.2} alignItems="center" mb={2}>
            <AutoAwesomeRoundedIcon sx={{ color: 'primary.main' }} />
            <Typography className="mono-label" sx={{ color: 'primary.main' }}>Agent setup</Typography>
          </Stack>
          <SummaryRow label="Mailbox" value={mailbox?.email || 'Connected mailbox'} />
          <Divider sx={{ borderColor: 'rgba(230,234,255,.12)' }} />
          <SummaryRow label="Finding conversations" value={`${s.specificTargets.length ? `${s.specificTargets.length} specific target${s.specificTargets.length === 1 ? '' : 's'} + ` : ''}${s.automaticDiscovery ? `last ${s.lookbackDays} days` : 'specific targets only'}`} />
          <Divider sx={{ borderColor: 'rgba(230,234,255,.12)' }} />
          <SummaryRow label="Mode" value={effectiveMode === 'draft' ? 'Draft in my mailbox' : 'Send automatically'} />
          {effectiveMode === 'send' && (
            <>
              <Divider sx={{ borderColor: 'rgba(230,234,255,.12)' }} />
              <SummaryRow
                label="Auto-send permission"
                value={(s.autoSendPolicy || 'approval') === 'approval'
                  ? 'Approve each conversation'
                  : (s.autoSendPolicy || 'approval') === 'allowlist'
                    ? 'Allow-list only'
                    : 'Anyone except blocked'}
              />
            </>
          )}
          <Divider sx={{ borderColor: 'rgba(230,234,255,.12)' }} />
          <SummaryRow label="Auto-create drafts" value={effectiveAutoCreateDrafts ? 'Enabled' : 'Off · review first'} />
          <Divider sx={{ borderColor: 'rgba(230,234,255,.12)' }} />
          <SummaryRow label="Frequency" value={s.cadence === 'custom' ? `Custom · ${s.minDays || 3}–${s.maxDays || 5} days` : `${cadenceCopy[s.cadence]?.label || 'Balanced'} · ${cadenceCopy[s.cadence]?.range || '3–5 business days'}`} />
          <Divider sx={{ borderColor: 'rgba(230,234,255,.12)' }} />
          <SummaryRow label="Maximum" value={s.continueUntilReply ? 'Until reply' : `${s.attempts} follow-ups`} />
          <Divider sx={{ borderColor: 'rgba(230,234,255,.12)' }} />
          <SummaryRow label="Smart timing" value={s.smartTiming ? 'Enabled' : 'Disabled'} />
          <Divider sx={{ borderColor: 'rgba(230,234,255,.12)' }} />
          <SummaryRow label="Thread history" value={s.includeThreadHistoryInDrafts ? 'Included in drafts' : 'Not included'} />
          <Divider sx={{ borderColor: 'rgba(230,234,255,.12)' }} />
          <SummaryRow label="Attachments" value={effectiveAttachments ? 'Copy when possible' : 'Disabled'} />
          <Divider sx={{ borderColor: 'rgba(230,234,255,.12)' }} />
          <SummaryRow label="Smart protections" value="Enabled" />
        </Box>
      </Box>
    </StepShell>
  );
}

export function OnboardingPage() {
  const { state, refreshAppState } = useApp();
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [upgradeReason, setUpgradeReason] = useState('');

  if (!state.user) {
    navigate('/signin');
    return null;
  }

  const openUpgrade = (reason) => {
    setUpgradeReason(reason || 'Upgrade to Pro to unlock this feature.');
  };

  const next = () => setStep((value) => Math.min(6, value + 1));
  const back = () => {
    if (step === 1) navigate('/signin');
    else setStep((value) => Math.max(1, value - 1));
  };

  let content;

  if (step === 1) content = <MailboxStep onBack={back} onNext={next} onUpgrade={openUpgrade} />;
  else if (step === 2) content = <TargetsStep onBack={back} onNext={next} onUpgrade={openUpgrade} />;
  else if (step === 3) content = <ModeStep onBack={back} onNext={next} onUpgrade={openUpgrade} />;
  else if (step === 4) content = <BehaviorStep onBack={back} onNext={next} onUpgrade={openUpgrade} />;
  else if (step === 5) content = <SafetyStep onBack={back} onNext={next} />;
  else content = <ActivateStep onBack={back} onReconnect={() => setStep(1)} />;

  return (
    <>
      {content}

      <UpgradeModal
        open={Boolean(upgradeReason)}
        onClose={() => setUpgradeReason('')}
        reason={upgradeReason}
        subscription={state.subscription}
        launchRef={state.pendingReferralCode}
        onUpgraded={refreshAppState}
      />
    </>
  );
}
